import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HiArrowLeft, HiExclamation, HiCheckCircle } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';

const CONFIRM_WORD = 'DELETE';

const DeleteAccountPage = () => {
  const navigate = useNavigate();
  const { currentUser, reauthenticate, deleteAccount, getPrimaryAuthProvider } = useAuth();

  const provider = useMemo(() => getPrimaryAuthProvider(), [getPrimaryAuthProvider, currentUser]);

  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [reauthed, setReauthed] = useState(false);
  const [reauthLoading, setReauthLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canSubmit =
    reauthed && confirmText.trim().toUpperCase() === CONFIRM_WORD && !deleting;

  const handlePasswordReauth = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error('Enter your password to continue');
      return;
    }
    setReauthLoading(true);
    try {
      await reauthenticate(password);
      setReauthed(true);
      toast.success('Verified');
    } catch (err) {
      toast.error(err.message || 'Could not verify password');
    } finally {
      setReauthLoading(false);
    }
  };

  const handlePopupReauth = async () => {
    setReauthLoading(true);
    try {
      await reauthenticate();
      setReauthed(true);
      toast.success('Identity verified');
    } catch (err) {
      if (err?.code !== 'auth/popup-closed-by-user') {
        toast.error(err.message || 'Verification failed');
      }
    } finally {
      setReauthLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!canSubmit) return;
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success('Account deleted');
      // AuthContext's onAuthStateChanged will fire → ProtectedRoute will
      // bounce us to /login automatically.
      navigate('/login');
    } catch (err) {
      setDeleting(false);
      toast.error(err.message || 'Could not delete account');
    }
  };

  const renderReauth = () => {
    if (reauthed) {
      return (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <HiCheckCircle className="text-emerald-600 text-lg" />
          <span className="text-sm font-medium text-emerald-800">
            Identity verified. You can continue below.
          </span>
        </div>
      );
    }

    if (provider === 'password') {
      return (
        <form onSubmit={handlePasswordReauth} className="space-y-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Current password
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-base mt-1 w-full"
              placeholder="Enter your password"
            />
          </label>
          <button
            type="submit"
            disabled={reauthLoading}
            className="btn-secondary text-sm disabled:opacity-50"
          >
            {reauthLoading ? 'Verifying…' : 'Verify password'}
          </button>
        </form>
      );
    }

    if (provider === 'google') {
      return (
        <button
          onClick={handlePopupReauth}
          disabled={reauthLoading}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          {reauthLoading ? 'Opening Google…' : 'Verify with Google'}
        </button>
      );
    }

    if (provider === 'apple') {
      return (
        <button
          onClick={handlePopupReauth}
          disabled={reauthLoading}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          {reauthLoading ? 'Opening Apple…' : 'Verify with Apple'}
        </button>
      );
    }

    return (
      <p className="text-sm text-ink-500">
        We couldn't determine your sign-in method. Please sign out and sign
        back in, then try again.
      </p>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-sm font-medium text-ink-500 hover:text-ink-700"
      >
        <HiArrowLeft className="text-base" />
        Back to settings
      </Link>

      <header>
        <h1 className="text-2xl font-bold text-ink-900">Delete Account</h1>
        <p className="text-sm text-ink-500 mt-1">
          This will permanently remove your access and personal data from
          coparent.
        </p>
      </header>

      <div className="rounded-xl bg-red-50 border border-red-200 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <HiExclamation className="text-red-600 text-xl" />
          <h2 className="font-bold text-red-800">This cannot be undone.</h2>
        </div>
        <p className="text-sm text-red-800">
          If you share a family with a co-parent, they will keep their copy of
          the schedule, expenses, and activities — you'll appear as a former
          co-parent in their history.
        </p>
        <p className="text-sm text-red-800">
          If you're the only parent in a family, that family's data will be
          removed entirely.
        </p>
      </div>

      <section className="card p-6 space-y-3">
        <h2 className="font-bold text-ink-900 text-[0.9375rem]">
          1. Verify your identity
        </h2>
        <p className="text-sm text-ink-500">
          For your security, confirm who you are before we delete your account.
        </p>
        {renderReauth()}
      </section>

      <section className="card p-6 space-y-3">
        <h2 className="font-bold text-ink-900 text-[0.9375rem]">
          2. Type {CONFIRM_WORD} to confirm
        </h2>
        <p className="text-sm text-ink-500">
          This prevents accidental deletion.
        </p>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={!reauthed}
          placeholder={CONFIRM_WORD}
          className="input-base w-full uppercase tracking-wide disabled:opacity-60"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck="false"
        />
      </section>

      <div className="flex items-center gap-3">
        <button
          onClick={handleDelete}
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {deleting ? 'Deleting account…' : 'Delete my account'}
        </button>
        <Link
          to="/settings"
          className="text-sm font-medium text-ink-500 hover:text-ink-700"
        >
          Cancel
        </Link>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
